import {
    Component,
    Input,
    OnInit,
    AfterViewInit,
    OnDestroy,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { AppStore, GroupsRepartitionProperties, RangeBegin } from '@app/models';
import { StoreProvider } from '@app/providers';
import { CountingService } from '@app/services';
import { CountingCircleStateService } from '@app/services/counting-circle-state.service';
import { Subscription } from 'rxjs';
import { CALENDAR_SELECT, CALENDAR_TYPE } from '@app/models/Calendar';
import Chart from 'chart.js/auto';

@Component({
    standalone: false,
    selector: 'app-counting-groups',
    templateUrl: './counting-groups.component.html',
    styleUrls: ['./counting-groups.component.scss'],
})
export class CountingGroupsComponent
    implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
    @Input() businessId!: string | number | undefined;
    @Input() format!: CALENDAR_SELECT;
    @Input() range!: RangeBegin;

    public data!: GroupsRepartitionProperties;
    public dataVs!: GroupsRepartitionProperties;

    @Input() calendarSelect!: CALENDAR_SELECT;
    @Input() calendarType!: CALENDAR_TYPE;

    public _value: number[] = [];
    public _valueVs: number[] = [];
    public _labels!: string[];
    public _labelsVs!: string[];

    public authorized = true;
    public emptyState = false;
    public isClosed = false;
    public loading = false;

    private $storeSubs!: Subscription;
    private dataSub?: Subscription;
    public app!: AppStore;

    public averageDisplay = {
        versus: 0,
        current: 0,
    };

    private charts: Chart[] = [];
    private viewInitialized = false;

    constructor(
        private $store: StoreProvider,
        private countingService: CountingService,
        private circleState: CountingCircleStateService
    ) {}

    ngOnInit() {
        this.$storeSubs = this.$store
            .listen('app')
            .subscribe((app) => (this.app = app));
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            (changes['businessId'] || changes['format'] || changes['range']) &&
            this.businessId != null &&
            this.format &&
            this.range
        ) {
            this.circleState.setLoading();
            this.loadData();
        }
    }

    private lastComparisonNbGroups = 0;

    private loadData(): void {
        this.loading = true;
        this.dataSub?.unsubscribe();

        this.dataSub = this.countingService
            .getGroupsRepartitionData(
                Number(this.businessId),
                this.format,
                this.range
            )
            .subscribe({
                next: (result: any) => {
                    this.data = result.current;
                    this.dataVs = result.versus;
                    this.lastComparisonNbGroups =
                        result.comparison?.nbGroups ?? 0;
                    this.isClosed = result.closed === true;

                    this.parseData();
                    this.loading = false;

                    this.emitCircleData();

                    setTimeout(() => this.renderCharts(), 0);
                },
                error: () => {
                    this.loading = false;
                    this.isClosed = false;
                    this.emptyState = true;
                },
            });
    }

    private emitCircleData(): void {
        if (this.isClosed) {
            this.circleState.emit({
                current: 0,
                versus: 0,
                variation: 0,
                icon: '/assets/svg/customers-group-dark.svg',
            });
            return;
        }

        if (!this.data || !this.dataVs) return;

        this.circleState.emit({
            current: this.data.nbGroups,
            versus: this.dataVs.nbGroups,
            variation: this.lastComparisonNbGroups ?? 0,
            icon: '/assets/svg/customers-group-dark.svg',
        });
    }

    private computeVariation(current: number, versus: number): number {
        if (!versus) return 0;
        return ((current - versus) / versus) * 100;
    }

    private parseData(): void {
        this.emptyState = false;
        this._value = [];
        this._valueVs = [];
        this._labels = [];
        this._labelsVs = [];

        if (
            this.isClosed ||
            (!this.data?.nbGroupsByLevel && !this.dataVs?.nbGroupsByLevel)
        ) {
            this.emptyState = true;
            return;
        }

        this._value = Object.values(this.data.nbGroupsByLevel);
        this._valueVs = Object.values(this.dataVs.nbGroupsByLevel);
        this._labels = Object.keys(this.data.nbGroupsByLevel);
        this._labelsVs = Object.keys(this.dataVs.nbGroupsByLevel);

        this.authorized =
            this.app!.props!.selectedLocalBusiness!.groupsCountingEnabled!;
        this.averageDisplay.current = this.data.averageGroups;
        this.averageDisplay.versus = this.dataVs.averageGroups;
    }

    ngAfterViewInit() {
        this.viewInitialized = true;
        // Si les données sont déjà arrivées (appel rapide), on rend ici aussi
        if (this.data && this.dataVs && !this.loading) {
            this.renderCharts();
        }
    }

    private renderCharts(): void {
        if (!this.viewInitialized) return;

        this.destroyCharts();
        if (this.emptyState || !this._labels?.length) return;

        const labelsList = this.getMaxArray();
        labelsList.forEach((labels, index) => {
            const canvas = document.getElementById(
                'groups_chart' + index
            ) as HTMLCanvasElement;
            if (!canvas) return;

            this.charts.push(
                new Chart(canvas, {
                    data: {
                        datasets: [
                            {
                                data: [this._value[index] ?? 0],
                                borderColor: '#e3a719',
                                backgroundColor: '#e3a719',
                                barThickness: 12,
                                borderRadius: {
                                    bottomRight: 25,
                                    topRight: 25,
                                },
                            },
                            {
                                data: [this._valueVs[index] ?? 0],
                                borderColor: '#024F5D',
                                backgroundColor: '#024F5D',
                                barThickness: 12,
                                borderRadius: {
                                    bottomRight: 25,
                                    topRight: 25,
                                },
                            },
                        ],
                        labels: [labels],
                    },
                    type: 'bar',
                    options: {
                        indexAxis: 'y',
                        elements: {
                            bar: {
                                borderWidth: 2,
                            },
                        },
                        responsive: true,
                        scales: {
                            y: {
                                ticks: {
                                    display: false,
                                    autoSkip: false,
                                    stepSize: 20,
                                },
                            },
                            x: {
                                beginAtZero: true,
                                display: false,
                                max: Math.max(
                                    ...this._value,
                                    ...this._valueVs,
                                    1
                                ),
                            },
                        },
                        maintainAspectRatio: false,
                        plugins: {
                            legend: { display: false },
                            title: { display: false },
                            datalabels: { display: false },
                            tooltip: { enabled: false },
                        },
                    },
                })
            );
        });
    }

    private destroyCharts(): void {
        this.charts.forEach((chart) => chart.destroy());
        this.charts = [];
    }

    getMaxArray() {
        return this._labels.length >= this._labelsVs.length
            ? this._labels
            : this._labelsVs;
    }

    ngOnDestroy(): void {
        this.$storeSubs?.unsubscribe();
        this.dataSub?.unsubscribe();
        this.destroyCharts();
    }
}
